import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import Header from 'components/Header';
import FillAppTabs from 'components/FillAppTabs';
import FillAppSubtab from 'components/FillAppSubtab';
import { FillAppCommentsContext, type TutorFillAppCommentsCtx } from 'components/FillAppField/commentsContext';
import { useFillAppSchema } from 'hooks/useFillAppSchema';
import { useFillAppColleges } from 'hooks/useFillAppColleges';
import type { College, CollegeChoice } from 'global/colleges';
import {
    type FillAppValues,
    type FillAppScreen,
    type FillAppSubtab as FillAppSubtabDef,
    type FillAppFieldComment,
    buildQidLocationMap,
    computeOverallProgress,
    computeScreenProgress,
    computeSubtabFilled,
    screenRequiredComplete,
    subtabIsVisible,
    subtabRequiredComplete,
    visibleQidsOfSubtab,
} from 'global/fillapp';

import './index.css';

const ADMIN_ROLES = new Set(['Admin', 'Super Admin', 'Staff']);

type FillAppPayload = {
    studentOrganizationId: number;
    studentName: string;
    studentEmail: string;
    organizationName: string;
    requestingTutorId: number;
    fillApp: {
        id: number;
        isEnabled: boolean;
        cycleYear: number;
        values: FillAppValues;
        sectionStatus: Record<string, string>;
        colleges: CollegeChoice[];
        fieldComments: FillAppFieldComment[];
        lastSavedAt: string | null;
    } | null;
};

// Mirrors the student view (and real Common App): the Courses & Grades screen
// lists which of the student's chosen colleges require the self-reported
// courses and which don't use them.
function CoursesGradesColleges({ choices, cycleYear, studentName }: {
    choices: CollegeChoice[];
    cycleYear: number;
    studentName: string;
}) {
    const { grid } = useFillAppColleges(cycleYear);
    if (!grid) return null;
    if (choices.length === 0) {
        return (
            <div className='fappReview__cgColleges fappReview__cgColleges--empty'>
                {studentName} hasn't added colleges yet, so there's nothing to check
                Courses &amp; Grades requirements against.
            </div>
        );
    }
    const byId = new Map(grid.colleges.map(c => [c.id, c]));
    const selected = choices.map(ch => byId.get(ch.id)).filter(Boolean) as College[];
    const required = selected.filter(c => c.coursesGrades).map(c => c.name);
    const notUsed = selected.filter(c => !c.coursesGrades).map(c => c.name);
    return (
        <div className='fappReview__cgColleges'>
            <div className='fappReview__cgRow'>
                <span className='fappReview__cgTag fappReview__cgTag--req'>
                    Required by {required.length}
                </span>
                <span className='fappReview__cgNames'>
                    {required.length > 0 ? required.join(', ') : 'None of their colleges'}
                </span>
            </div>
            <div className='fappReview__cgRow'>
                <span className='fappReview__cgTag'>
                    Not used by {notUsed.length}
                </span>
                <span className='fappReview__cgNames'>
                    {notUsed.length > 0 ? notUsed.join(', ') : 'None of their colleges'}
                </span>
            </div>
        </div>
    );
}

type SubtabStatus = 'complete' | 'in-progress' | 'empty' | 'selected-dot';

function computeSubtabStatus(subtab: FillAppSubtabDef, values: FillAppValues): SubtabStatus {
    if (subtabRequiredComplete(subtab, values)) return 'complete';
    const { filled } = computeSubtabFilled(subtab, values);
    return filled === 0 ? 'empty' : 'in-progress';
}

function computeScreenPct(screen: FillAppScreen, values: FillAppValues): number {
    return computeScreenProgress(screen, values).percent;
}

const STATUS_SYMBOL: Record<SubtabStatus, string> = {
    'complete': '✓',
    'in-progress': '◐',
    'empty': '○',
    'selected-dot': '●',
};

// Comments whose field is currently hidden by conditional answers or inactive
// repeating-group slots — surfaced above the form so they can't be lost.
function HiddenFieldComments({ subtab, values, comments, onDelete }: {
    subtab: FillAppSubtabDef;
    values: FillAppValues;
    comments: FillAppFieldComment[];
    onDelete: (commentId: number) => Promise<void>;
}) {
    const subtabQids = useMemo(() => new Set(subtab.fields.map(f => f.qid)), [subtab]);
    const visible = useMemo(() => visibleQidsOfSubtab(subtab, values), [subtab, values]);
    const hidden = comments.filter(c => subtabQids.has(c.qid) && !visible.has(c.qid) && !c.isResolved);
    if (hidden.length === 0) return null;
    const labelOf = (qid: string) =>
        (subtab.fields.find(f => f.qid === qid)?.label || qid).replace(/<[^>]+>/g, '');
    return (
        <div className='fappReview__hiddenComments'>
            <b>{hidden.length} comment{hidden.length > 1 ? 's' : ''} on fields not currently shown</b>
            {hidden.map(c => (
                <div key={c.id} className='fappReview__hiddenComment'>
                    <span><i>{labelOf(c.qid)}</i> — {c.body}</span>
                    <button type='button' onClick={() => onDelete(c.id).catch(() => undefined)}>Delete</button>
                </div>
            ))}
        </div>
    );
}

function FillAppReview() {
    const { studentOrganizationId } = useParams<{ studentOrganizationId: string }>();
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const isAdmin = user?.role && ADMIN_ROLES.has(user.role);

    const [payload, setPayload] = useState<FillAppPayload | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [enabling, setEnabling] = useState(false);
    // Field comments: single source of truth, seeded from the GET payload;
    // commentsByQid / badge counts / ctx value are memoized derivations.
    const [comments, setComments] = useState<FillAppFieldComment[]>([]);

    // Follow the student's own cycle rather than a hardcoded year; the payload
    // arrives after the first render, so fall back until it does.
    const { schema, loading: schemaLoading } = useFillAppSchema(payload?.fillApp?.cycleYear);

    const loadFillApp = () => {
        if (!studentOrganizationId) return;
        return axios.get(`fillapp/tutor/review/${studentOrganizationId}/`)
            .then(res => {
                setPayload(res.data);
                setComments(res.data.fillApp?.fieldComments || []);
                setError(null);
            })
            .catch(err => {
                setError(err?.response?.data?.message || 'Failed to load College Apps.');
            });
    };

    useEffect(() => {
        let cancelled = false;
        if (!studentOrganizationId) return;
        axios.get(`fillapp/tutor/review/${studentOrganizationId}/`)
            .then(res => {
                if (cancelled) return;
                setPayload(res.data);
                setComments(res.data.fillApp?.fieldComments || []);
            })
            .catch(err => {
                if (!cancelled) setError(err?.response?.data?.message || 'Failed to load College Apps.');
            });
        return () => { cancelled = true; };
    }, [studentOrganizationId]);

    const fillAppId = payload?.fillApp?.id;
    const commentsCtx = useMemo<TutorFillAppCommentsCtx>(() => {
        const byQid: Record<string, FillAppFieldComment[]> = {};
        for (const c of comments) (byQid[c.qid] = byQid[c.qid] || []).push(c);
        return {
            commentsByQid: byQid,
            currentTutorId: payload?.requestingTutorId ?? null,
            addComment: (qid, body) =>
                axios.post(`fillapp/tutor/fieldcomments/${fillAppId}/create/`, { qid, body })
                    .then(res => setComments(prev => [...prev, res.data.comment])),
            updateComment: (commentId, body) =>
                axios.post(`fillapp/tutor/fieldcomment/${commentId}/`, { body })
                    .then(res => setComments(prev => prev.map(c => (c.id === commentId ? res.data.comment : c)))),
            deleteComment: (commentId) =>
                axios.post(`fillapp/tutor/fieldcomment/${commentId}/delete/`)
                    .then(() => setComments(prev => prev.filter(c => c.id !== commentId))),
        };
    }, [comments, fillAppId, payload?.requestingTutorId]);

    // Unresolved-count badges for the sidebar, keyed by screen/subtab.
    const qidLocation = useMemo(() => (schema ? buildQidLocationMap(schema) : null), [schema]);
    const openCounts = useMemo(() => {
        const bySubtab: Record<string, number> = {};
        const byScreen: Record<string, number> = {};
        const byQid: Record<string, number> = {};
        if (!qidLocation) return { bySubtab, byScreen, byQid };
        for (const c of comments) {
            if (c.isResolved) continue;
            const loc = qidLocation.get(c.qid);
            if (!loc) continue;
            bySubtab[loc.subtabId] = (bySubtab[loc.subtabId] || 0) + 1;
            byScreen[loc.screenId] = (byScreen[loc.screenId] || 0) + 1;
            byQid[c.qid] = (byQid[c.qid] || 0) + 1;
        }
        return { bySubtab, byScreen, byQid };
    }, [comments, qidLocation]);

    const enableFillApp = () => {
        if (!studentOrganizationId || enabling) return;
        setEnabling(true);
        axios.post(`fillapp/tutor/enable/${studentOrganizationId}/`)
            .then(() => loadFillApp())
            .catch(err => setError(err?.response?.data?.message || 'Failed to enable College Apps.'))
            .finally(() => setEnabling(false));
    };

    const [expandedScreenId, setExpandedScreenId] = useState<string | null>(null);
    const [activeSubtabId, setActiveSubtabId] = useState<string | null>(null);

    useEffect(() => {
        if (!schema || expandedScreenId) return;
        const first = schema.screens[0];
        if (first) {
            setExpandedScreenId(first.id);
            setActiveSubtabId(first.subtabs[0]?.id || null);
        }
    }, [schema, expandedScreenId]);

    const activeScreen = useMemo(() => {
        if (!schema || !expandedScreenId) return null;
        return schema.screens.find(s => s.id === expandedScreenId) || null;
    }, [schema, expandedScreenId]);

    const activeSubtab = useMemo(() => {
        if (!activeScreen) return null;
        return activeScreen.subtabs.find(st => st.id === activeSubtabId) || activeScreen.subtabs[0] || null;
    }, [activeScreen, activeSubtabId]);

    const switchScreen = (screenId: string) => {
        if (!schema) return;
        const screen = schema.screens.find(s => s.id === screenId);
        if (!screen) return;
        setExpandedScreenId(screenId);
        setActiveSubtabId(screen.subtabs[0]?.id || null);
    };

    if (error) {
        return (
            <div className='fappReview'>
                <Header title='Common App' prevRoutes={[{ name: 'College Apps', path: '/college-apps' }]} />
                <FillAppTabs studentOrganizationId={studentOrganizationId!} active='commonapp' />
                <div className='fappReview__error'>{error}</div>
            </div>
        );
    }

    if (!payload || !schema || schemaLoading) {
        return (
            <div className='fappReview'>
                <Header title='Common App' prevRoutes={[{ name: 'College Apps', path: '/college-apps' }]} />
                <FillAppTabs studentOrganizationId={studentOrganizationId!} active='commonapp' />
                <div className='fappReview__loading'>Loading…</div>
            </div>
        );
    }

    // No FillApp row yet — show enable empty state instead of the form.
    if (!payload.fillApp) {
        return (
            <div className='fappReview'>
                <Helmet><title>{payload.studentName} - College Apps Review</title></Helmet>
                <Header
                    title='Common App'
                    prevRoutes={[
                        { name: 'College Apps', path: '/college-apps' },
                        { name: payload.studentName, path: `/college-apps/review/${studentOrganizationId}` },
                    ]}
                />
                <FillAppTabs studentOrganizationId={studentOrganizationId!} active='commonapp' />
                <div className='fappReview__emptyState'>
                    <h2>College Apps not yet enabled</h2>
                    <p>{payload.studentName} doesn't have College Apps enabled for {payload.organizationName} yet.</p>
                    {isAdmin && (
                        <button
                            className='fappReview__enableBtn'
                            disabled={enabling}
                            onClick={enableFillApp}
                        >
                            {enabling ? 'Enabling…' : 'Enable College Apps for this student'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const values = payload.fillApp.values;

    return (
        <div className='fappReview'>
            <Helmet><title>{payload.studentName} - College Apps Review</title></Helmet>
            <Header
                title='Common App'
                prevRoutes={[
                    { name: 'College Apps', path: '/college-apps' },
                    { name: payload.studentName, path: `/college-apps/review/${studentOrganizationId}` },
                ]}
            />
            <FillAppTabs studentOrganizationId={studentOrganizationId!} active='commonapp' />
            {!payload.fillApp.isEnabled && (
                <div className='fappReview__disabledBanner'>
                    College Apps is currently disabled. The student cannot edit it until re-enabled.
                </div>
            )}

            <div className='fappReview__layout'>
                <aside className='fappReview__nav'>
                    <div className='fappReview__navHead'>
                        <div className='fappReview__navHeadRow'>
                            <h3>Application</h3>
                            <span className='fappReview__overallPct'>{computeOverallProgress(schema, values).percent}%</span>
                        </div>
                        <div className='fappReview__overallBar'>
                            <span style={{ width: `${computeOverallProgress(schema, values).percent}%` }} />
                        </div>
                    </div>
                    {schema.screens.map(screen => {
                        const expanded = screen.id === expandedScreenId;
                        const pct = computeScreenPct(screen, values);
                        const complete = screenRequiredComplete(screen, values);
                        return (
                            <React.Fragment key={screen.id}>
                                <div
                                    className={`fappReview__sectionItem ${expanded ? 'fappReview__sectionItem--expanded' : ''}`}
                                    onClick={() => switchScreen(screen.id)}
                                >
                                    <svg className='fappReview__sectionChevron' viewBox='0 0 24 24' fill='currentColor'>
                                        <path d='M9.4 6L8 7.4l4.6 4.6L8 16.6 9.4 18l6-6z' />
                                    </svg>
                                    <div className='fappReview__sectionInfo'>
                                        <span className='fappReview__sectionLabel'>{screen.label}</span>
                                        {(openCounts.byScreen[screen.id] || 0) > 0 && (
                                            <span className='fappReview__commentBadge'>{openCounts.byScreen[screen.id]}</span>
                                        )}
                                        {complete
                                            ? <span className='fappReview__sectionDone'>✓</span>
                                            : <span className='fappReview__sectionPct'>{pct}%</span>}
                                    </div>
                                </div>
                                {expanded && (
                                    <div className='fappReview__subtabList'>
                                        {screen.subtabs.filter(st => subtabIsVisible(st, values)).map(st => {
                                            const isActive = activeSubtabId === st.id;
                                            const status: SubtabStatus = isActive
                                                ? 'selected-dot'
                                                : computeSubtabStatus(st, values);
                                            return (
                                                <div
                                                    key={st.id}
                                                    className={`fappReview__subtabNav ${isActive ? 'fappReview__subtabNav--selected' : ''}`}
                                                    onClick={() => setActiveSubtabId(st.id)}
                                                >
                                                    <span className={`fappReview__subtabStatus fappReview__subtabStatus--${status}`}>
                                                        {STATUS_SYMBOL[status]}
                                                    </span>
                                                    <span className='fappReview__subtabLabel'>{st.title || st.label}</span>
                                                    {(openCounts.bySubtab[st.id] || 0) > 0 && (
                                                        <span className='fappReview__commentBadge'>{openCounts.bySubtab[st.id]}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </aside>

                <div className='fappReview__formArea'>
                    <div className='fappReview__infoBanner'>
                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                            <circle cx='12' cy='12' r='10' />
                            <path d='M12 8v4M12 16h.01' />
                        </svg>
                        Read-only view. To make changes, the student must edit it from their account.
                    </div>

                    {activeScreen?.id === 'courses-grades' && (
                        <CoursesGradesColleges
                            choices={payload.fillApp.colleges || []}
                            cycleYear={payload.fillApp.cycleYear}
                            studentName={payload.studentName}
                        />
                    )}

                    {activeSubtab && activeScreen && (
                        <>
                            <div className='fappReview__heading'>
                                <div className='fappReview__headingScreen'>{activeScreen.label}</div>
                                <h2 className='fappReview__headingSubtab'>{activeSubtab.title || activeSubtab.label}</h2>
                            </div>
                            <HiddenFieldComments
                                subtab={activeSubtab}
                                values={values}
                                comments={comments}
                                onDelete={commentsCtx.deleteComment}
                            />
                            <FillAppCommentsContext.Provider value={commentsCtx}>
                                <FillAppSubtab
                                    subtab={activeSubtab}
                                    values={values}
                                    choiceGroups={schema.choiceGroups}
                                    readOnly
                                    openCommentsByQid={openCounts.byQid}
                                />
                            </FillAppCommentsContext.Provider>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FillAppReview;
